import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Calendar, Clock, User, MessageSquare, Check, X, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface LateCancellationRequest {
  id: string;
  booking_id: string;
  client_id: string;
  practitioner_id: string;
  client_reason: string;
  status: string;
  practitioner_response?: string;
  created_at: string;
  responded_at?: string;
  booking: {
    start_time: string;
    end_time: string;
    service: {
      service_name: string;
    };
    client: {
      first_name: string;
      last_name: string;
    };
  };
}

export function LateCancellationRequestsList() {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const { user } = useAuth();
  const [requests, setRequests] = useState<LateCancellationRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchLateCancellationRequests();
  }, [user?.id]);

  const fetchLateCancellationRequests = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('late_cancellation_requests')
        .select(`
          *,
          booking:bookings(
            start_time,
            end_time,
            service:services(service_name),
            client:clients(first_name, last_name)
          )
        `)
        .eq('practitioner_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching late cancellation requests:', error);
        toast({
          title: t('lateCancellationRequests.loadFailed'),
          description: t('lateCancellationRequests.loadError'),
          variant: "destructive",
        });
        return;
      }

      setRequests((data || []) as LateCancellationRequest[]);
    } catch (error) {
      console.error('Error fetching late cancellation requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponse = async (requestId: string, action: 'approved' | 'rejected') => {
    const confirmMessage = action === 'approved' 
      ? t('lateCancellationRequests.approveConfirm')
      : t('lateCancellationRequests.rejectConfirm');

    if (!confirm(confirmMessage)) {
      return;
    }

    setProcessingId(requestId);
    
    try {
      const { data, error } = await supabase.functions.invoke('handle-late-cancellation-request', {
        body: {
          requestId,
          action,
          practitionerResponse: responseText[requestId] || ''
        }
      });

      if (error) {
        console.error('Error processing late cancellation request:', error);
        toast({
          title: t('lateCancellationRequests.operationFailed'),
          description: error.message || t('lateCancellationRequests.updateError'),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: action === 'approved' 
          ? t('lateCancellationRequests.approved') 
          : t('lateCancellationRequests.rejected'),
        description: action === 'approved' 
          ? t('lateCancellationRequests.approvedDesc')
          : t('lateCancellationRequests.rejectedDesc'),
      });

      // Refresh the requests list
      fetchLateCancellationRequests();
      
      // Clear the response text
      setResponseText(prev => {
        const newState = { ...prev };
        delete newState[requestId];
        return newState;
      });
    } catch (error) {
      console.error('Error processing late cancellation request:', error);
      toast({
        title: t('lateCancellationRequests.operationFailed'),
        description: t('lateCancellationRequests.networkError'),
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return t('lateCancellationRequests.status.pending');
      case 'approved':
        return t('lateCancellationRequests.status.approved');
      case 'rejected':
        return t('lateCancellationRequests.status.rejected');
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-gray-500">{t('lateCancellationRequests.loading')}</div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">{t('lateCancellationRequests.noPending')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{t('lateCancellationRequests.title')}</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchLateCancellationRequests}
          disabled={isLoading}
        >
          {t('lateCancellationRequests.refresh')}
        </Button>
      </div>

      {requests.map((request) => (
        <Card key={request.id} className="border-l-4 border-l-red-500">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">
                  {request.booking?.service?.service_name}
                </CardTitle>
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                  <User className="h-4 w-4" />
                  <span>
                    {request.booking?.client?.first_name} {request.booking?.client?.last_name}
                  </span>
                </div>
              </div>
              <Badge className={getStatusColor(request.status)}>
                {getStatusText(request.status)}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Session time */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-2">{t('lateCancellationRequests.sessionTime')}</h4>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(request.booking.start_time), 'yyyy-MM-dd EEEE')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>
                    {format(new Date(request.booking.start_time), 'HH:mm')} - 
                    {format(new Date(request.booking.end_time), 'HH:mm')}
                  </span>
                </div>
              </div>
            </div>

            {/* Client reason */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {t('lateCancellationRequests.clientReason')}
              </h4>
              <p className="text-sm text-gray-700">{request.client_reason}</p>
            </div>

            {/* Warning message for pending requests */}
            {request.status === 'pending' && (
              <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <p className="font-medium mb-1">{t('lateCancellationRequests.urgentNotice')}</p>
                    <p>{t('lateCancellationRequests.urgentDescription')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Practitioner response section */}
            {request.status === 'pending' && (
              <div className="space-y-3">
                <Label htmlFor={`response-${request.id}`}>
                  {t('lateCancellationRequests.practitionerResponse')}
                </Label>
                <Textarea
                  id={`response-${request.id}`}
                  value={responseText[request.id] || ''}
                  onChange={(e) => 
                    setResponseText(prev => ({ ...prev, [request.id]: e.target.value }))
                  }
                  placeholder={t('lateCancellationRequests.responsePlaceholder')}
                  className="min-h-[80px]"
                />
                
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResponse(request.id, 'rejected')}
                    disabled={processingId === request.id}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t('lateCancellationRequests.reject')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleResponse(request.id, 'approved')}
                    disabled={processingId === request.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    {t('lateCancellationRequests.approve')}
                  </Button>
                </div>
              </div>
            )}

            {/* Show practitioner response if exists */}
            {request.practitioner_response && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <h4 className="font-medium mb-2">{t('lateCancellationRequests.yourResponse')}</h4>
                <p className="text-sm text-gray-700">{request.practitioner_response}</p>
              </div>
            )}

            {/* Request timestamp */}
            <div className="text-xs text-gray-500 text-right">
              {t('lateCancellationRequests.requestTime')} {format(new Date(request.created_at), 'yyyy-MM-dd HH:mm')}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
